import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EMPLOYEE_MOVEMENT_LABELS,
  PAY_FREQUENCY_LABELS,
  PAY_METHOD_LABELS,
  voidReasonSchema,
  type EmployeeDetailDTO,
  type EmployeeMovementDTO,
  type ProductDTO,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { ArrowLeft, Beef, CalendarCheck, Ellipsis, HandCoins, KeyRound, Pencil, Plus, ReceiptText, Search, Trash2, Wallet } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useProducts } from '../../lib/catalog';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { addDaysIso, centsToPesosInput, pesosToCents, todayIso, toInternalQty } from '../../lib/units';
import { balanceText, EmployeeFormDialog, StatusBadge } from './EmployeesPage';

type DialogKind = 'advance' | 'goods' | 'payroll' | 'pay' | 'edit' | 'pin';
const hours = (min: number) => `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;

function useRefresh() {
  const qc = useQueryClient();
  return () => Promise.all(['employees', 'cash', 'stock', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

/** Ficha del empleado: saldo, adelantos, mercadería, liquidación y pago del sueldo, y asistencia. */
export function EmployeePage() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ['employees', id], queryFn: () => api.get<EmployeeDetailDTO>(`/employees/${id}`) });
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [voiding, setVoiding] = useState<EmployeeMovementDTO | null>(null);
  const [tab, setTab] = useState<'moves' | 'attendance'>('moves');
  const { me, can } = useSession();
  const canSalary = can('employees.manage');
  const hasAttendance = me.tenant.modules.includes('ATTENDANCE');
  const e = q.data;
  const b = e ? balanceText(e) : null;

  return (
    <>
      <Link to="/empleados" className="bf-back">
        <ArrowLeft size={16} /> Empleados
      </Link>
      <PageHeader
        title={
          <>
            {e?.name ?? 'Empleado'} {e && <StatusBadge e={e} />}
          </>
        }
        subtitle={
          e ? [e.position, e.branchName, e.startDate && `desde el ${formatDate(e.startDate)}`].filter(Boolean).join(' · ') || undefined : undefined
        }
        actions={
          e && (
            <>
              <Button icon={<Wallet />} onClick={() => setDialog('advance')}>
                Adelanto
              </Button>
              <Button icon={<Beef />} onClick={() => setDialog('goods')}>
                Se llevó mercadería
              </Button>
              {canSalary && (
                <>
                  <Button icon={<ReceiptText />} onClick={() => setDialog('payroll')}>
                    Liquidar sueldo
                  </Button>
                  <Button variant="primary" icon={<HandCoins />} onClick={() => setDialog('pay')}>
                    Pagar
                  </Button>
                </>
              )}
              <Menu>
                <MenuTrigger asChild>
                  <button className="bf-icon-btn" aria-label="Más acciones">
                    <Ellipsis />
                  </button>
                </MenuTrigger>
                <MenuContent>
                  {canSalary && (
                    <MenuItem icon={<Pencil />} onSelect={() => setDialog('edit')}>
                      Editar ficha
                    </MenuItem>
                  )}
                  {hasAttendance && (
                    <MenuItem icon={<KeyRound />} onSelect={() => setDialog('pin')}>
                      {e.hasPin ? 'Cambiar PIN de asistencia' : 'Poner PIN de asistencia'}
                    </MenuItem>
                  )}
                </MenuContent>
              </Menu>
            </>
          )
        }
      />
      {!e ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <>
          {hasAttendance && !e.hasPin && (
            <Callout tone="info">Para que marque entrada y salida en el mostrador, ponele un PIN (menú ⋯ → “Poner PIN de asistencia”).</Callout>
          )}
          <div className="bf-kpi-grid">
            {b && <Kpi label={b.label} tone={(e.balanceCents ?? 0) > 0 ? 'amber' : 'celeste'} icon={<Wallet />} value={b.value} />}
            <Kpi
              label="Adelantos desde la última liquidación"
              tone="red"
              icon={<HandCoins />}
              value={formatMoney(e.pendingAdvancesCents)}
              delta={e.lastPeriodTo ? `Liquidado hasta el ${formatDate(e.lastPeriodTo)}` : 'Nunca se liquidó'}
            />
            {e.salaryCents !== null && (
              <Kpi
                label="Sueldo acordado"
                tone="green"
                icon={<ReceiptText />}
                value={formatMoney(e.salaryCents)}
                delta={PAY_FREQUENCY_LABELS[e.payFrequency]}
              />
            )}
          </div>
          <div className="bf-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'moves'} onClick={() => setTab('moves')}>
              Movimientos
            </button>
            {hasAttendance && (
              <button type="button" role="tab" aria-selected={tab === 'attendance'} onClick={() => setTab('attendance')}>
                Asistencia
              </button>
            )}
          </div>
          {tab === 'moves' ? (
            <Card>
              <CardBody flush>
                {e.movements.length === 0 ? (
                  <EmptyState icon={<Wallet />} title="Sin movimientos todavía" />
                ) : (
                  <div className="bf-table-wrap">
                    <table className="bf-table">
                      <tbody>
                        {e.movements.map((m) => (
                          <tr key={m.id} style={{ opacity: m.voided ? 0.45 : 1 }}>
                            <td className="bf-cell-sub" style={{ whiteSpace: 'nowrap' }}>
                              {formatDateTime(m.createdAt)}
                            </td>
                            <td>
                              <span className="bf-cell-main">{EMPLOYEE_MOVEMENT_LABELS[m.type]}</span>
                              {m.method && <span className="bf-cell-sub"> · {PAY_METHOD_LABELS[m.method]}</span>} {m.voided && <Badge>Anulado</Badge>}
                              <div className="bf-cell-sub">
                                {[
                                  m.periodFrom && m.periodTo && `${formatDate(m.periodFrom)} al ${formatDate(m.periodTo)}`,
                                  m.description,
                                  m.createdByName,
                                  m.voided && m.voidReason && `Anulado: ${m.voidReason}`,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </div>
                            </td>
                            <td className={`bf-num ${m.amountCents < 0 ? '' : 'bf-text-success'}`} style={{ textAlign: 'right', fontWeight: 600 }}>
                              {m.amountCents < 0 ? `− ${formatMoney(-m.amountCents)}` : `+ ${formatMoney(m.amountCents)}`}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {!m.voided && (
                                <Button size="sm" variant="ghost" onClick={() => setVoiding(m)}>
                                  Anular
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardHeader title="Últimos 30 días" subtitle="Entradas y salidas marcadas con su PIN en el mostrador." />
              <CardBody flush>
                {e.attendance.length === 0 ? (
                  <EmptyState icon={<CalendarCheck />} title="No marcó entradas ni salidas" />
                ) : (
                  <table className="bf-table">
                    <thead>
                      <tr>
                        <th>Día</th>
                        <th>Entradas y salidas</th>
                        <th style={{ textAlign: 'right' }}>Horas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {e.attendance.map((d) => (
                        <tr key={d.date}>
                          <td className="bf-cell-main">{formatDate(d.date)}</td>
                          <td>
                            {d.shifts.map((s, i) => (
                              <span key={i} style={{ marginRight: 12 }}>
                                {s.in ?? '—'} a {s.out ?? <Badge tone="warning">sin salida</Badge>}
                              </span>
                            ))}
                          </td>
                          <td className="bf-num" style={{ textAlign: 'right' }}>
                            {d.minutes ? hours(d.minutes) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardBody>
            </Card>
          )}
          {dialog === 'advance' && <AdvanceDialog e={e} onClose={() => setDialog(null)} />}
          {dialog === 'goods' && <GoodsDialog e={e} onClose={() => setDialog(null)} />}
          {dialog === 'payroll' && <PayrollDialog e={e} onClose={() => setDialog(null)} />}
          {dialog === 'pay' && <PayDialog e={e} onClose={() => setDialog(null)} />}
          {dialog === 'pin' && <PinDialog e={e} onClose={() => setDialog(null)} />}
          {dialog === 'edit' && <EmployeeFormDialog employee={e} onClose={() => setDialog(null)} />}
          {voiding && <VoidDialog m={voiding} onClose={() => setVoiding(null)} />}
        </>
      )}
    </>
  );
}

function Actions({ busy, onClose, label, icon }: { busy: boolean; onClose: () => void; label: string; icon?: React.ReactNode }) {
  return (
    <div className="bf-form-actions">
      <Button onClick={onClose} disabled={busy}>
        Cancelar
      </Button>
      <Button type="submit" variant="primary" loading={busy} icon={icon}>
        {label}
      </Button>
    </div>
  );
}

function useSubmit(onClose: () => void) {
  const refresh = useRefresh();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, done?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
      done?.();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return { error, setError, busy, run };
}

function AdvanceDialog({ e, onClose }: { e: EmployeeDetailDTO; onClose: () => void }) {
  const toast = useToast();
  const { error, setError, busy, run } = useSubmit(onClose);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'CASH' | 'TRANSFER' | 'OTHER'>('CASH');
  const [description, setDescription] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} title={`Adelanto a ${e.name}`} description="Se le descuenta al liquidar el sueldo.">
      <form
        className="bf-form"
        noValidate
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          const cents = pesosToCents(amount);
          if (!cents || cents <= 0) return setError('Poné el monto.');
          void run(
            () =>
              api.post('/employee-movements', {
                employeeId: e.id,
                type: 'ADVANCE',
                amountCents: cents,
                method,
                description: description.trim() || null,
              }),
            () => toast.success('Adelanto anotado', method === 'CASH' ? 'Salió de la caja.' : undefined),
          );
        }}
      >
        <div className="bf-form-row">
          <Field label="Monto">
            <Input className="bf-input--lg" inputMode="decimal" value={amount} onChange={(ev) => setAmount(ev.target.value)} autoFocus />
          </Field>
          <Field label="Cómo" hint={method === 'CASH' ? 'Sale de la caja abierta.' : undefined}>
            <Select value={method} onChange={(ev) => setMethod(ev.target.value as typeof method)}>
              <option value="CASH">Efectivo de la caja</option>
              <option value="TRANSFER">Transferencia</option>
              <option value="OTHER">Otro</option>
            </Select>
          </Field>
        </div>
        <Field label="Detalle (opcional)">
          <Input value={description} onChange={(ev) => setDescription(ev.target.value)} placeholder="Ej.: vale del viernes" />
        </Field>
        {error && <Callout tone="danger">{error}</Callout>}
        <Actions busy={busy} onClose={onClose} label="Anotar adelanto" />
      </form>
    </Dialog>
  );
}

function GoodsDialog({ e, onClose }: { e: EmployeeDetailDTO; onClose: () => void }) {
  const toast = useToast();
  const { error, setError, busy, run } = useSubmit(onClose);
  const products = useProducts();
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<{ p: ProductDTO; qty: string }[]>([]);
  const [pricing, setPricing] = useState<'COST' | 'DISCOUNT'>('COST');
  const [discount, setDiscount] = useState('20');
  const matches = search.trim()
    ? (products.data ?? [])
        .filter((p) => p.isActive && p.sellable && !lines.some((l) => l.p.id === p.id) && p.name.toLowerCase().includes(search.trim().toLowerCase()))
        .slice(0, 8)
    : [];
  const pct = Math.min(100, Math.max(0, Number(discount.replace(',', '.')) || 0));
  const estimate = lines.reduce((a, l) => {
    const q = toInternalQty(l.p.unitType, l.qty || '0') ?? 0;
    const price = l.p.priceCents ?? 0;
    return a + (l.p.unitType === 'WEIGHT' ? (q * price) / 1000 : q * price);
  }, 0);

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`${e.name} se llevó mercadería`}
      description="Sale del stock y se le anota para descontar del sueldo."
      wide
    >
      <form
        className="bf-form"
        noValidate
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          const items: { productId: string; qty: number }[] = [];
          for (const l of lines) {
            const q = toInternalQty(l.p.unitType, l.qty);
            if (!q || q <= 0) return setError(`Poné cuánto de ${l.p.name}.`);
            items.push({ productId: l.p.id, qty: q });
          }
          if (!items.length) return setError('Agregá lo que se llevó.');
          void run(
            () => api.post('/employee-goods', { employeeId: e.id, pricing, discountPercent: pct, items }),
            () => toast.success('Mercadería anotada'),
          );
        }}
      >
        <Field label="Agregar producto">
          <div style={{ position: 'relative' }}>
            <Input value={search} onChange={(ev) => setSearch(ev.target.value)} placeholder="Ej.: asado" autoFocus />
            {matches.length > 0 && (
              <div className="bf-suggest">
                {matches.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setLines((ls) => [...ls, { p, qty: '' }]);
                      setSearch('');
                    }}
                  >
                    <Search aria-hidden /> {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Field>
        {lines.length > 0 && (
          <table className="bf-table">
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.p.id}>
                  <td className="bf-cell-main">{l.p.name}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Input
                      inputMode="decimal"
                      value={l.qty}
                      onChange={(ev) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, qty: ev.target.value } : x)))}
                      placeholder={l.p.unitType === 'WEIGHT' ? 'kg' : 'u.'}
                      aria-label={`Cantidad de ${l.p.name}`}
                      style={{ maxWidth: 110, marginLeft: 'auto' }}
                    />
                  </td>
                  <td style={{ width: 48 }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 />}
                      aria-label="Sacar"
                      onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="bf-form-row">
          <Field label="Se le cobra">
            <Select value={pricing} onChange={(ev) => setPricing(ev.target.value as typeof pricing)}>
              <option value="COST">A precio de costo</option>
              <option value="DISCOUNT">Precio de venta con descuento</option>
            </Select>
          </Field>
          {pricing === 'DISCOUNT' && (
            <Field label="Descuento (%)">
              <Input inputMode="decimal" value={discount} onChange={(ev) => setDiscount(ev.target.value)} />
            </Field>
          )}
        </div>
        {pricing === 'DISCOUNT' && estimate > 0 && (
          <p className="bf-cell-sub" style={{ margin: 0 }}>
            Se le anotan unos {formatMoney(Math.round(estimate * (1 - pct / 100)))}.
          </p>
        )}
        {pricing === 'COST' && (
          <p className="bf-cell-sub" style={{ margin: 0 }}>
            Se le anota lo que costó esa mercadería (según los lotes que se descuentan).
          </p>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        <Actions busy={busy} onClose={onClose} label="Anotar" />
      </form>
    </Dialog>
  );
}

function PayrollDialog({ e, onClose }: { e: EmployeeDetailDTO; onClose: () => void }) {
  const toast = useToast();
  const { error, setError, busy, run } = useSubmit(onClose);
  const firstOfMonth = todayIso().slice(0, 8) + '01';
  const [from, setFrom] = useState(e.lastPeriodTo ? addDaysIso(e.lastPeriodTo, 1) : firstOfMonth);
  const [to, setTo] = useState(todayIso());
  const [salary, setSalary] = useState(centsToPesosInput(e.salaryCents ?? 0));
  const [extras, setExtras] = useState<{ description: string; amount: string }[]>([]);
  const salaryCents = pesosToCents(salary || '0') ?? 0;
  const extrasCents = extras.reduce((a, x) => a + (pesosToCents(x.amount || '0') ?? 0), 0);
  const result = (e.balanceCents ?? 0) + salaryCents + extrasCents;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} title={`Liquidar sueldo de ${e.name}`} wide>
      <form
        className="bf-form"
        noValidate
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          const ex: { description: string; amountCents: number }[] = [];
          for (const x of extras) {
            const c = pesosToCents(x.amount);
            if (!x.description.trim() || !c) return setError('Completá cada extra con detalle y monto.');
            ex.push({ description: x.description.trim(), amountCents: c });
          }
          void run(
            () => api.post('/employee-payroll', { employeeId: e.id, periodFrom: from, periodTo: to, salaryCents, extras: ex }),
            () => toast.success('Sueldo liquidado', `Queda para pagar ${formatMoney(Math.max(0, result))}.`),
          );
        }}
      >
        <div className="bf-form-row">
          <Field label="Desde">
            <Input type="date" value={from} max={to} onChange={(ev) => ev.target.value && setFrom(ev.target.value)} />
          </Field>
          <Field label="Hasta">
            <Input type="date" value={to} min={from} onChange={(ev) => ev.target.value && setTo(ev.target.value)} />
          </Field>
          <Field label="Sueldo del período">
            <Input inputMode="decimal" value={salary} onChange={(ev) => setSalary(ev.target.value)} />
          </Field>
        </div>
        {extras.map((x, i) => (
          <div className="bf-form-row" key={i}>
            <Field label="Extra">
              <Input
                value={x.description}
                onChange={(ev) => setExtras((xs) => xs.map((y, j) => (j === i ? { ...y, description: ev.target.value } : y)))}
                placeholder="Horas extra, premio, feriado…"
              />
            </Field>
            <Field label="Monto">
              <Input
                inputMode="decimal"
                value={x.amount}
                onChange={(ev) => setExtras((xs) => xs.map((y, j) => (j === i ? { ...y, amount: ev.target.value } : y)))}
              />
            </Field>
          </div>
        ))}
        <div>
          <Button size="sm" icon={<Plus />} onClick={() => setExtras((xs) => [...xs, { description: '', amount: '' }])}>
            Agregar extra
          </Button>
        </div>
        <div className="bf-close-summary">
          <div>
            <span>Sueldo + extras</span>
            <strong className="bf-num">{formatMoney(salaryCents + extrasCents)}</strong>
          </div>
          <div>
            <span>{(e.balanceCents ?? 0) < 0 ? 'Menos lo adelantado' : 'Más lo que ya se le debía'}</span>
            <strong className="bf-num">
              {(e.balanceCents ?? 0) < 0 ? '− ' : ''}
              {formatMoney(Math.abs(e.balanceCents ?? 0))}
            </strong>
          </div>
          <div>
            <span>{result >= 0 ? 'Queda para pagar' : 'Sigue adelantado'}</span>
            <strong className="bf-num">{formatMoney(Math.abs(result))}</strong>
          </div>
        </div>
        {error && <Callout tone="danger">{error}</Callout>}
        <Actions busy={busy} onClose={onClose} label="Liquidar" icon={<ReceiptText />} />
      </form>
    </Dialog>
  );
}

function PayDialog({ e, onClose }: { e: EmployeeDetailDTO; onClose: () => void }) {
  const toast = useToast();
  const { error, setError, busy, run } = useSubmit(onClose);
  const [amount, setAmount] = useState(centsToPesosInput(Math.max(0, e.balanceCents ?? 0)));
  const [method, setMethod] = useState<'CASH' | 'TRANSFER' | 'OTHER'>('CASH');
  const cents = pesosToCents(amount || '0') ?? 0;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Pagar a ${e.name}`}
      description={
        (e.balanceCents ?? 0) > 0 ? `Se le debe ${formatMoney(e.balanceCents ?? 0)}.` : 'No se le debe nada: si le pagás, queda como adelanto.'
      }
    >
      <form
        className="bf-form"
        noValidate
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          if (cents <= 0) return setError('Poné el monto.');
          void run(
            () => api.post('/employee-payments', { employeeId: e.id, amountCents: cents, method }),
            () => toast.success('Pago registrado', method === 'CASH' ? 'Salió de la caja.' : undefined),
          );
        }}
      >
        <div className="bf-form-row">
          <Field label="Monto">
            <Input className="bf-input--lg" inputMode="decimal" value={amount} onChange={(ev) => setAmount(ev.target.value)} autoFocus />
          </Field>
          <Field label="Cómo" hint={method === 'CASH' ? 'Sale de la caja abierta.' : undefined}>
            <Select value={method} onChange={(ev) => setMethod(ev.target.value as typeof method)}>
              <option value="CASH">Efectivo de la caja</option>
              <option value="TRANSFER">Transferencia</option>
              <option value="OTHER">Otro</option>
            </Select>
          </Field>
        </div>
        {cents > Math.max(0, e.balanceCents ?? 0) && cents > 0 && (
          <Callout tone="warning">Es más de lo que se le debe: la diferencia queda como adelanto.</Callout>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        <Actions busy={busy} onClose={onClose} label="Pagar" icon={<HandCoins />} />
      </form>
    </Dialog>
  );
}

function PinDialog({ e, onClose }: { e: EmployeeDetailDTO; onClose: () => void }) {
  const toast = useToast();
  const { error, setError, busy, run } = useSubmit(onClose);
  const [pin, setPin] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`PIN de asistencia de ${e.name}`}
      description="Con este PIN marca la entrada y la salida en el mostrador. Que lo elija él y no lo comparta."
    >
      <form
        className="bf-form"
        noValidate
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          if (!/^\d{4,6}$/.test(pin)) return setError('El PIN tiene entre 4 y 6 números.');
          void run(
            () => api.put(`/employees/${e.id}/pin`, { pin }),
            () => toast.success('PIN guardado'),
          );
        }}
      >
        <Field label="PIN (4 a 6 números)">
          <Input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={pin}
            onChange={(ev) => setPin(ev.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
          />
        </Field>
        {error && <Callout tone="danger">{error}</Callout>}
        <Actions busy={busy} onClose={onClose} label="Guardar PIN" />
      </form>
    </Dialog>
  );
}

function VoidDialog({ m, onClose }: { m: EmployeeMovementDTO; onClose: () => void }) {
  const { error, setError, busy, run } = useSubmit(onClose);
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Anular ${EMPLOYEE_MOVEMENT_LABELS[m.type].toLowerCase()} de ${formatMoney(Math.abs(m.amountCents))}`}
      description={
        m.type === 'GOODS'
          ? 'La mercadería vuelve al stock.'
          : m.method === 'CASH' && (m.type === 'ADVANCE' || m.type === 'PAYMENT')
            ? 'Fue en efectivo: la plata vuelve a la caja abierta.'
            : 'Queda registrado quién lo anuló y por qué.'
      }
    >
      <form
        className="bf-form"
        noValidate
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          const parsed = voidReasonSchema.safeParse({ reason });
          if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Contá el motivo.');
          void run(() => api.post(`/employee-movements/${m.id}/void`, parsed.data));
        }}
      >
        <Field label="Motivo">
          <Textarea rows={2} value={reason} onChange={(ev) => setReason(ev.target.value)} autoFocus />
        </Field>
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button type="submit" variant="danger" loading={busy}>
            Anular
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
